import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { CheckCircle2, PhoneCall, Users, Clock, ArrowLeft } from 'lucide-react';
import { trackPageView } from '../lib/analytics';

const STEPS = [
  { icon: Clock, title: 'We review your request', text: 'Our team reads your details straight away, usually within a few minutes during business hours.' },
  { icon: Users, title: 'We shortlist local agents', text: 'We match you with 2 to 3 trusted agents who know your suburb and property type.' },
  { icon: PhoneCall, title: 'We call you', text: 'Expect a friendly call or email from FindMeAgent shortly to confirm the next step. No obligation.' },
];

export default function ThankYou() {
  const location = useLocation();
  const submitted = location.state?.submitted === true;
  const isAgent = location.state?.type === 'agent';

  useEffect(() => {
    if (!submitted) return;
    document.title = 'Thank You | FindMeAgent';
    trackPageView('/thank-you', 'Thank You | FindMeAgent');
  }, [submitted]);

  // Only reachable after a real submission - prevents bots/bookmarks from
  // registering false conversions on this URL.
  if (!submitted) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="thankyou-page" data-testid="thank-you-page">
      <header className="thankyou-topbar">
        <Link to="/" className="header-logo" data-testid="thank-you-logo-link">
          <div className="logo-icon">
            <span className="logo-text">FM</span>
          </div>
          <span className="logo-name">FINDMEAGENT</span>
        </Link>
      </header>

      <main className="thankyou-main">
        <div className="thankyou-card">
          <div className="thankyou-icon" aria-hidden="true">
            <CheckCircle2 size={44} />
          </div>
          <p className="thankyou-eyebrow">Request received</p>
          <h1 className="thankyou-title" data-testid="thank-you-title">
            {isAgent ? 'Thanks for applying to join FindMeAgent' : 'Thank you! We are on it.'}
          </h1>
          <p className="thankyou-lead">
            {isAgent
              ? 'Our partnerships team will review your application and be in touch shortly.'
              : 'Your details are with our team. We are already matching you with trusted local agents in your area.'}
          </p>

          {!isAgent && (
            <ol className="thankyou-steps">
              {STEPS.map((step, i) => (
                <li key={step.title} className="thankyou-step" style={{ animationDelay: `${0.15 * (i + 1)}s` }}>
                  <span className="thankyou-step-icon"><step.icon size={20} /></span>
                  <div>
                    <h2 className="thankyou-step-title">{step.title}</h2>
                    <p className="thankyou-step-text">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="thankyou-actions">
            <Link to="/" className="thankyou-btn" data-testid="thank-you-home-button">
              <ArrowLeft size={18} /> Back to home
            </Link>
            <a href="mailto:info@findmeagent.com.au" className="thankyou-link" data-testid="thank-you-email-link">
              Questions? info@findmeagent.com.au
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
